import * as THREE from 'three';

/**
 * Guardián procedural: ancla visual del score y feedback al recolectar.
 * No se toca para sumar puntos — celebra cada orbe y muestra el marcador X/50 encima.
 *
 * Extensión herramienta: sustituir por GLB con `loadGlbCharacter(url)`
 * o `?character=/assets/guardian.glb` (ver public/assets/README.md).
 */

const DEFAULT_GLB_SCALE = 0.5;

export function createCharacter() {
  const root = new THREE.Group();
  root.name = 'character';

  const bodyMat = new THREE.MeshStandardMaterial({
    color: 0x2ec4b6,
    roughness: 0.45,
    metalness: 0.15,
  });
  const accentMat = new THREE.MeshStandardMaterial({
    color: 0xff9f1c,
    roughness: 0.4,
    metalness: 0.2,
    emissive: 0x331a00,
    emissiveIntensity: 0.35,
  });

  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.18, 0.35, 6, 12), bodyMat);
  body.position.y = 0.45;
  body.castShadow = true;

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 16, 16), bodyMat);
  head.position.y = 0.85;

  const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.03, 8, 8), accentMat);
  eyeL.position.set(-0.06, 0.88, 0.13);
  const eyeR = eyeL.clone();
  eyeR.position.x = 0.06;

  const armL = new THREE.Mesh(new THREE.CapsuleGeometry(0.05, 0.22, 4, 8), accentMat);
  armL.position.set(-0.28, 0.5, 0);
  armL.rotation.z = 0.4;
  const armR = armL.clone();
  armR.position.x = 0.28;
  armR.rotation.z = -0.4;

  const pedestal = new THREE.Mesh(
    new THREE.CylinderGeometry(0.22, 0.28, 0.06, 20),
    new THREE.MeshStandardMaterial({ color: 0x0b1f2a, roughness: 0.7, metalness: 0.1 }),
  );
  pedestal.position.y = 0.03;

  const scoreSprite = _makeScoreSprite('0/50');
  scoreSprite.position.set(0, 1.25, 0);
  scoreSprite.scale.set(0.55, 0.22, 1);

  root.add(pedestal, body, head, eyeL, eyeR, armL, armR, scoreSprite);

  let bob = 0;
  let celebrateT = 0;
  let celebratePower = 0;
  let baseY = 0.05;

  const mixerStub = {
    update(dt) {
      bob += dt * 4;
      if (celebrateT > 0) {
        celebrateT = Math.max(0, celebrateT - dt);
        const t = celebrateT / 0.35;
        const hop = Math.sin((1 - t) * Math.PI) * (0.08 + celebratePower * 0.04);
        root.position.y = baseY + hop;
        root.scale.setScalar(1 + Math.sin((1 - t) * Math.PI) * (0.12 + celebratePower * 0.06));
        root.rotation.y += dt * (4 + celebratePower * 2);
        accentMat.emissiveIntensity = 0.35 + (1 - t) * (0.8 + celebratePower * 0.3);
      } else {
        root.position.y = baseY + Math.sin(bob) * 0.04;
        root.scale.setScalar(1);
        root.rotation.y = Math.sin(bob * 0.5) * 0.25;
        accentMat.emissiveIntensity = 0.35;
      }
      armL.rotation.x = Math.sin(bob * 1.5) * 0.5;
      armR.rotation.x = Math.cos(bob * 1.5) * 0.5;
    },
    celebrate(combo = 1) {
      celebratePower = Math.min(5, Math.max(1, combo));
      celebrateT = 0.35;
    },
    setScore(score, targetScore) {
      _paintScoreSprite(scoreSprite, `${score}/${targetScore}`);
    },
  };

  return { root, mixer: mixerStub, source: 'procedural' };
}

/**
 * Hook de extensión: carga un GLB y expone la misma API que el Guardián procedural
 * (`update`, `celebrate`, `setScore`) para no romper el loop de juego.
 *
 * @param {string} url ruta pública, p. ej. `/assets/guardian.glb`
 * @param {{ manager?: import('three').LoadingManager, scale?: number, scoreY?: number }} [opts]
 * @returns {Promise<{ root: THREE.Object3D, mixer: object, source: 'glb', clips: THREE.AnimationClip[] }>}
 */
export async function loadGlbCharacter(url, opts = {}) {
  if (!url || typeof url !== 'string') {
    throw new Error('loadGlbCharacter: url requerida (ej. /assets/guardian.glb)');
  }

  const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
  const loader = new GLTFLoader(opts.manager);
  const gltf = await loader.loadAsync(url);

  const wrapper = new THREE.Group();
  wrapper.name = 'character-glb';
  const model = gltf.scene;
  model.name = 'glb-model';
  const scale = Number.isFinite(opts.scale) ? opts.scale : DEFAULT_GLB_SCALE;
  model.scale.setScalar(scale);
  wrapper.add(model);

  const scoreY = Number.isFinite(opts.scoreY) ? opts.scoreY : 1.25;
  const scoreSprite = _makeScoreSprite('0/50');
  scoreSprite.position.set(0, scoreY, 0);
  scoreSprite.scale.set(0.55, 0.22, 1);
  wrapper.add(scoreSprite);

  let animMixer = null;
  const clips = gltf.animations || [];
  if (clips.length) {
    animMixer = new THREE.AnimationMixer(model);
    animMixer.clipAction(clips[0]).play();
  }

  let celebrateT = 0;
  let celebratePower = 0;
  const baseScale = 1;

  const mixer = {
    update(dt) {
      animMixer?.update(dt);
      if (celebrateT > 0) {
        celebrateT = Math.max(0, celebrateT - dt);
        const t = celebrateT / 0.35;
        const hop = Math.sin((1 - t) * Math.PI) * (0.06 + celebratePower * 0.03);
        wrapper.position.y = hop;
        wrapper.scale.setScalar(baseScale + Math.sin((1 - t) * Math.PI) * (0.1 + celebratePower * 0.04));
        wrapper.rotation.y += dt * (3 + celebratePower * 1.5);
      } else {
        wrapper.position.y = 0;
        wrapper.scale.setScalar(baseScale);
      }
    },
    celebrate(combo = 1) {
      celebratePower = Math.min(5, Math.max(1, combo));
      celebrateT = 0.35;
    },
    setScore(score, targetScore) {
      _paintScoreSprite(scoreSprite, `${score}/${targetScore}`);
    },
  };

  return { root: wrapper, mixer, source: 'glb', clips };
}

/**
 * Resuelve personaje: GLB si `characterUrl` es usable; si falla, procedural.
 * @param {string | null | undefined} characterUrl
 */
export async function resolveCharacter(characterUrl) {
  if (!characterUrl) return createCharacter();
  try {
    const loaded = await loadGlbCharacter(characterUrl);
    console.info('[character] GLB cargado:', characterUrl);
    return loaded;
  } catch (err) {
    console.warn('[character] GLB falló → Guardián procedural:', err?.message || err);
    return createCharacter();
  }
}

function _makeScoreSprite(label) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 96;
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  sprite.name = 'score-home';
  sprite.userData.canvas = canvas;
  sprite.userData.texture = tex;
  _paintScoreSprite(sprite, label);
  return sprite;
}

function _paintScoreSprite(sprite, label) {
  const canvas = sprite.userData.canvas;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = 'rgba(6, 16, 24, 0.82)';
  _roundRect(ctx, 8, 12, canvas.width - 16, canvas.height - 24, 18);
  ctx.fill();
  ctx.strokeStyle = '#2ec4b6';
  ctx.lineWidth = 4;
  ctx.stroke();
  ctx.fillStyle = '#e8f4f2';
  ctx.font = 'bold 44px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, canvas.width / 2, canvas.height / 2 + 2);
  sprite.userData.texture.needsUpdate = true;
}

function _roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
